import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Grid,
    FormControlLabel,
    Checkbox,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Controller, useFormContext } from 'react-hook-form';

const ChildForm = ({ index, onRemove }) => {
    const { control, formState: { errors } } = useFormContext();

    // Get errors for this specific child
    const childErrors = errors.children?.[index] || {};

    const residesWithOptions = [
        { value: 'self', label: 'Self' },
        { value: 'Mom', label: 'Mom' },
        { value: 'Dad', label: 'Dad' },
        { value: 'Petitioner', label: 'Petitioner' },
        { value: 'Respondent', label: 'Respondent' },
        { value: 'Other', label: 'Other' }
    ];

    return (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Child #{index + 1}
                </Typography>
                <IconButton
                    type="button"
                    onClick={onRemove}
                    size="small"
                    color="error"
                    sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>

            {/*
              Phase 3b: required-field relaxation. Per-field rules retain
              data-correctness checks (length minimums, character pattern,
              date sanity) but only fire when a value is provided. Empty
              children fields save fine — interview agent or user fills
              over time.
            */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Controller
                        name={`children.${index}.firstName`}
                        control={control}
                        rules={{
                            minLength: {
                                value: 2,
                                message: 'First name must be at least 2 characters'
                            },
                            pattern: {
                                value: /^[A-Za-z\s'-]+$/,
                                message: 'First name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="First Name"
                                error={!!childErrors.firstName}
                                helperText={childErrors.firstName?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`children.${index}.middleName`}
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[A-Za-z\s'-]*$/,
                                message: 'Middle name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Middle Name"
                                error={!!childErrors.middleName}
                                helperText={childErrors.middleName?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`children.${index}.lastName`}
                        control={control}
                        rules={{
                            minLength: {
                                value: 2,
                                message: 'Last name must be at least 2 characters'
                            },
                            pattern: {
                                value: /^[A-Za-z\s'-]+$/,
                                message: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Last Name"
                                error={!!childErrors.lastName}
                                helperText={childErrors.lastName?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`children.${index}.birthDate`}
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (!value) return true; // Empty is allowed — no longer required.
                                const birthDate = new Date(value);
                                const today = new Date();
                                const maxAge = new Date();
                                maxAge.setFullYear(today.getFullYear() - 99); // Reasonable max age for child in divorce

                                if (birthDate > today) {
                                    return 'Birth date cannot be in the future';
                                }

                                if (birthDate < maxAge) {
                                    return 'Child cannot be older than 99 years';
                                }

                                return true;
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Birth Date"
                                type="date"
                                error={!!childErrors.birthDate}
                                helperText={childErrors.birthDate?.message}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                size="small"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`children.${index}.residesWith`}
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth size="small" error={!!childErrors.residesWith}>
                                <InputLabel>Resides With</InputLabel>
                                <Select
                                    {...field}
                                    label="Resides With"
                                >
                                    {residesWithOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {childErrors.residesWith && (
                                    <FormHelperText>{childErrors.residesWith.message}</FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Controller
                        name={`children.${index}.supportNeeded`}
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={field.onChange}
                                        size="small"
                                    />
                                }
                                label="Support Needed"
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ChildForm;